import { useEffect, useRef } from 'react';

// A SHEET'S KEYBOARD CONTRACT — lifted out of the customer account sheet when
// the request wizard's leave dialog became the second one (2026-10-08), and
// into lib/ when the app's Drawer became the third (2026-10-09), so none of
// them can drift apart. Returns the ref for the sheet element. `onClose` may
// be omitted: Escape then does nothing here, and the content owns its way out.

export function useSheetFocus(onClose?: () => void) {
  const sheet = useRef<HTMLDivElement>(null);

  // Escape shuts it, and the sheet takes focus when it opens — a dialog that
  // cannot be dismissed from the keyboard is a trap on a phone with a keyboard
  // attached, and one that never takes focus leaves the reader behind it.
  //
  // AND TAB STAYS INSIDE. `aria-modal="true"` is a PROMISE to assistive tech
  // that the rest of the page is inert; it does not make it so. Tab walked
  // straight out into the page behind, which for a screen-reader user is worse
  // than no dialog at all — the announcement says everything else is gone and
  // the keyboard says otherwise (issue 20, 2026-09-15). Focus is also returned
  // to whatever opened the sheet, so closing does not dump the caret at the
  // top of the document.
  const opener = useRef<HTMLElement | null>(null);

  useEffect(() => {
    // FIRST RUN ONLY. The effect re-runs whenever the parent re-renders while
    // the sheet is open, and by then `activeElement` is the sheet itself — so
    // closing dropped the caret on the sheet that had just gone rather than on
    // the *You* button (claude[bot], 2026-09-15). Captured here rather than in
    // the render body because renderToStaticMarkup has no `document`.
    if (opener.current === null) opener.current = (document.activeElement as HTMLElement | null) ?? null;
    sheet.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose?.(); return; }
      if (e.key !== 'Tab') return;
      const box = sheet.current;
      if (!box) return;
      const stops = [...box.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])',
      )].filter((el) => el.offsetParent !== null || el === box);
      if (stops.length === 0) { e.preventDefault(); box.focus(); return; }
      const first = stops[0]!;
      const last = stops[stops.length - 1]!;
      const on = document.activeElement;
      if (e.shiftKey && (on === first || on === box)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && on === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      opener.current?.focus?.();
    };
  }, [onClose]);

  return sheet;
}
